import { staleResponse } from '../api/forumErrors';

function immutable(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(immutable);
    Object.freeze(value);
  }
  return value;
}

/** Bounded LRU of successful pages. Invalidated flights can never refill it. */
export function createForumCache({ maxEntries = 20, ttl = 30000, now = Date.now } = {}) {
  const entries = new Map(), pending = new Map();
  function invalidate(matches = () => true, { flights = true } = {}) {
    entries.forEach((entry, key) => { if (matches(key, entry.tags)) entries.delete(key); });
    if (flights) pending.forEach((flight, key) => { if (matches(key, flight.tags)) pending.delete(key); });
  }
  function peek(key) {
    const entry = entries.get(key);
    if (!entry) return null;
    if (entry.expiresAt <= now()) { entries.delete(key); return null; }
    entries.delete(key); entries.set(key, entry);
    return entry.value;
  }
  function read(key, fetch, { tags = [], refresh = false } = {}) {
    if (pending.has(key)) return pending.get(key).promise;
    const cached = peek(key);
    if (!refresh && cached !== null) return Promise.resolve(cached);
    const flight = { tags: tags.slice(), promise: null };
    flight.promise = Promise.resolve().then(fetch).then(value => {
      if (pending.get(key) !== flight) throw staleResponse();
      entries.delete(key);
      entries.set(key, { value: immutable(value), tags: flight.tags, expiresAt: now() + ttl });
      while (entries.size > maxEntries) entries.delete(entries.keys().next().value);
      return value;
    }).finally(() => { if (pending.get(key) === flight) pending.delete(key); });
    pending.set(key, flight);
    return flight.promise;
  }
  // Seed an authoritative alias synchronously. Two callers sharing the same
  // clamped response must not invalidate each other's asynchronous seed flight.
  function seed(key, value, { tags = [] } = {}) {
    pending.delete(key); entries.delete(key);
    entries.set(key, { value: immutable(value), tags: tags.slice(), expiresAt: now() + ttl });
    while (entries.size > maxEntries) entries.delete(entries.keys().next().value);
  }
  return { read, peek, invalidate, seed, get size() { return entries.size; } };
}
